import type { AxiosError, InternalAxiosRequestConfig } from 'axios'
import axios from 'axios'
import { v4 as uuidv4 } from 'uuid'

import { appLanguage } from '@/composables/useAppLanguage'
import auth from '@/services/auth'

interface BirtaAxiosRequestConfig extends InternalAxiosRequestConfig {
  enableCaching?: boolean
  birtaAuthRetryAttempted?: boolean
}

interface BirtaAuthRecoveryError extends AxiosError {
  isBirtaAuthRecoveryRedirect?: boolean
}

const markAsAuthRecoveryRedirectError = (
  error: AxiosError,
): BirtaAuthRecoveryError => {
  const authRecoveryError = error as BirtaAuthRecoveryError
  authRecoveryError.isBirtaAuthRecoveryRedirect = true

  return authRecoveryError
}

export const isAuthRecoveryRedirectError = (
  error: unknown,
): boolean => {
  return (
    typeof error === 'object'
    && error !== null
    && 'isBirtaAuthRecoveryRedirect' in error
    && error.isBirtaAuthRecoveryRedirect === true
  )
}

const redirectToAuthentication = async (
  error: AxiosError,
): Promise<never> => {
  const authRecoveryError = markAsAuthRecoveryRedirectError(error)

  try {
    await auth.beginAuthenticationToBirtaEntry()
  }
  catch (redirectError) {
    console.error('Unable to start interactive authentication', redirectError)
  }

  throw authRecoveryError
}

const createRequestService = ({
  baseURL,
}: {
  baseURL: string
}) => {
  const instance = axios.create({
    baseURL,
    headers: {
      'Cache-Control': 'no-cache',
    },
  })

  instance.interceptors.request.use(
    (requestConfig: BirtaAxiosRequestConfig) => {
      if (requestConfig.enableCaching) {
        requestConfig.headers.delete?.('Cache-Control')
        requestConfig.headers.delete?.('Pragma')
        delete requestConfig.enableCaching
      }

      requestConfig.headers.traceid = uuidv4()
      requestConfig.headers.originUrl = window.location.href
      requestConfig.headers['Accept-Language'] = appLanguage.value

      if (auth.token) {
        requestConfig.headers.Authorization = `Bearer ${auth.token}`
      }

      return requestConfig
    },
  )

  instance.interceptors.response.use(
    response => response,
    async (error: AxiosError) => {
      const requestConfig = error.config as BirtaAxiosRequestConfig | undefined

      if (!requestConfig || error.response?.status !== 401) {
        throw error
      }

      if (requestConfig.birtaAuthRetryAttempted) {
        return redirectToAuthentication(error)
      }

      requestConfig.birtaAuthRetryAttempted = true

      const didRenewToken = await auth.trySignInSilent()

      if (!didRenewToken) {
        return redirectToAuthentication(error)
      }

      // The request interceptor attaches the renewed token before replay.
      return instance(requestConfig)
    },
  )

  // TODO: Add scoped 404 recovery only for Birta Initialize and Package endpoints.
  // Search and other lookup endpoints must not reset the Birta session on a 404.

  return instance
}

export default createRequestService({
  baseURL: import.meta.env.VITE_API_BASE_URI,
})

// Legacy CCP v3 routes that are not mounted under /api.
export const RequestServiceWithoutApiPrefix = createRequestService({
  baseURL: import.meta.env.VITE_API_BASE_URI.replace(/\/api\/?$/, ''),
})
